import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validadores personalizados para el módulo Dripping
 * 
 * Incluye validaciones específicas para:
 * - Configuración de canales de comunicación
 * - Prioridades de envío
 * - Formato de variables en plantillas
 * - Rangos de fechas
 */
export class DrippingValidators {
  
  /**
   * Valida que al menos un canal esté habilitado
   * @returns Validación error si ningún canal está activo
   */
  static atLeastOneChannelEnabled(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const channels = control.value;
      
      const hasEnabled = channels?.email?.enabled ||
                        channels?.sms?.enabled ||
                        channels?.whatsapp?.enabled ||
                        channels?.call?.enabled;
      
      return hasEnabled ? null : { noChannelEnabled: true };
    };
  }
  
  /**
   * Valida que las prioridades de los canales sean únicas
   * @returns Validación error si hay prioridades duplicadas
   */
  static uniquePriorities(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const channels = control.value;
      if (!channels) return null;
      
      const priorities: number[] = [];
      
      if (channels.email?.enabled) priorities.push(channels.email.priority);
      if (channels.sms?.enabled) priorities.push(channels.sms.priority);
      if (channels.whatsapp?.enabled) priorities.push(channels.whatsapp.priority);
      if (channels.call?.enabled) priorities.push(channels.call.priority);
      
      const uniquePriorities = new Set(priorities);
      
      return priorities.length === uniquePriorities.size 
        ? null 
        : { duplicatePriorities: true };
    };
  }
  
  /**
   * Valida que todos los canales habilitados tengan hora programada
   * @returns Validación error si algún canal habilitado no tiene scheduledTime
   */
  static allEnabledChannelsHaveScheduledTime(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const channels = control.value;
      if (!channels) return null;
      
      const enabledChannels = Object.entries(channels).filter(
        ([_, ch]: [string, any]) => ch.enabled
      );
      
      if (enabledChannels.length === 0) return null;
      
      const missingScheduledTime = enabledChannels.some(
        ([_, ch]: [string, any]) => !ch.scheduledTime || ch.scheduledTime.trim() === ''
      );
      
      return missingScheduledTime 
        ? { channelsNeedScheduledTime: 'Todos los canales habilitados deben tener hora programada' }
        : null;
    };
  }
  
  /**
   * Valida que el código esté en MAYÚSCULAS
   * @returns Validación error si el código no está en uppercase
   */
  static uppercaseCode(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) return null;
      
      return value === value.toUpperCase() 
        ? null 
        : { notUppercase: true };
    };
  }
  
  /**
   * Valida el formato de variables {{variable}} en plantillas
   * @returns Validación error si hay variables con formato inválido
   */
  static validVariableFormat(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      if (!value) return null;
      
      // Regex para validar formato {{variable}}
      const regex = /\{\{([^}]+)\}\}/g;
      const matches = value.match(regex);
      
      if (!matches) return null;
      
      // Validar que todas las variables tengan formato correcto
      const invalidVariables = matches.filter((match: string) => {
        const varName = match.replace(/\{\{|\}\}/g, '').trim();
        return varName.length === 0 || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(varName);
      });
      
      return invalidVariables.length === 0 
        ? null 
        : { invalidVariableFormat: { variables: invalidVariables } };
    };
  }
  
  /**
   * Valida que la fecha de inicio sea anterior a la fecha de fin
   * @param startField - Nombre del campo de fecha inicio
   * @param endField - Nombre del campo de fecha fin
   * @returns Validación error si el rango es inválido
   */
  static dateRange(startField: string, endField: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const start = control.get(startField)?.value;
      const end = control.get(endField)?.value;
      
      if (!start || !end) return null;
      
      return new Date(start) <= new Date(end) 
        ? null 
        : { invalidDateRange: true };
    };
  }

  /**
   * Valida el rango de días (minDays <= maxDays cuando maxDays está definido)
   * Si maxDays es null o undefined, se considera sin límite y es válido
   * @returns Validación error si minDays > maxDays
   */
  static validDaysRange(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const minDays = control.get('minDays')?.value;
      const maxDays = control.get('maxDays')?.value;
      
      // Si maxDays es null/undefined, se considera sin límite - válido
      if (maxDays === null || maxDays === undefined) return null;
      
      // minDays debe ser >= 0
      if (minDays < 0) {
        return { invalidMinDays: 'El mínimo de días no puede ser negativo' };
      }
      
      // maxDays debe ser >= minDays
      if (maxDays < minDays) {
        return { 
          invalidDaysRange: 'El máximo de días debe ser mayor o igual al mínimo',
          minDays,
          maxDays
        };
      }
      
      return null;
    };
  }

  /**
   * Valida la configuración de frequency en canales
   * Si frequency > 1, el intervalo debe ser >= MIN_INTERVAL (5 minutos)
   * @param minInterval - Intervalo mínimo permitido
   * @returns Validación error si la configuración es inválida
   */
  static validFrequencyConfig(minInterval: number = 5): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const frequency = control.get('frequency')?.value;
      const frequencyInterval = control.get('frequencyInterval')?.value;
      const enabled = control.get('enabled')?.value;
      
      // Solo validar si el canal está habilitado
      if (!enabled) return null;
      
      // Si frequency > 1, validar intervalo
      if (frequency > 1) {
        if (!frequencyInterval || frequencyInterval < minInterval) {
          return { 
            invalidFrequencyInterval: `El intervalo debe ser al menos ${minInterval} minutos`,
            minInterval
          };
        }
      }
      
      // Validar que frequency esté en rango válido
      if (frequency < 1 || frequency > 10) {
        return { 
          invalidFrequency: 'La frecuencia debe estar entre 1 y 10'
        };
      }
      
      return null;
    };
  }

  /**
   * Valida que los horarios de ejecución estén dentro del horario permitido
   * @param startHour - Hora de inicio del horario operativo (ej: 8)
   * @param endHour - Hora de fin del horario operativo (ej: 21)
   * @returns Validación error si el horario está fuera del rango
   */
  static validOperatingHours(startHour: number = 8, endHour: number = 21): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const scheduledTime = control.value;
      
      if (!scheduledTime) return null;
      
      const [hours] = scheduledTime.split(':').map(Number);
      
      if (hours < startHour || hours >= endHour) {
        return { 
          outsideOperatingHours: `El horario debe estar entre ${startHour}:00 y ${endHour}:00`,
          startHour,
          endHour
        };
      }
      
      return null;
    };
  }
}
